import { Box, VStack, Heading, type BoxProps } from '@chakra-ui/react';
import { useMemo } from 'react';
import { MatchdayResultsList } from '@/components/MatchdayResultsList';
import { getRecentMatchdayWindow, isRecentMatchdayResult } from '@/helpers/fixtures/matchdayVisibility';
import { Card } from './Card';
import type { gameResult } from '@/models/gameUpdate';

interface ResultListCardProps extends Omit<BoxProps, 'children'> {
  userData: any;
  gameResults: gameResult[];
  currentMatchday: number;
  maxW?: BoxProps['maxW'];
  /** Symmetric horizontal padding for matchday headers and match rows. */
  listPadX?: BoxProps['px'];
  scrollContained?: boolean;
  hideHeading?: boolean;
  expandable?: boolean;
  singleLineTeamNames?: boolean;
}

export function ResultListCard({
  userData,
  gameResults,
  currentMatchday,
  maxW,
  listPadX = 0,
  scrollContained = true,
  hideHeading = false,
  expandable = true,
  singleLineTeamNames = false,
  ...cardProps
}: ResultListCardProps) {
  const { recent, older } = useMemo(() => {
    const matchdayWindow = getRecentMatchdayWindow(currentMatchday);
    const recentResults: gameResult[] = [];
    const olderResults: gameResult[] = [];
    for (const result of gameResults) {
      if (isRecentMatchdayResult(result, matchdayWindow)) {
        recentResults.push(result);
      } else {
        olderResults.push(result);
      }
    }
    return { recent: recentResults, older: olderResults };
  }, [gameResults, currentMatchday]);

  const list = (
    <>
      <MatchdayResultsList
        results={recent}
        svg
        expandable={expandable}
        nameLines={singleLineTeamNames ? 1 : undefined}
      />
      {older.length > 0 ? (
        <MatchdayResultsList
          results={older}
          svg
          expandable={expandable}
          nameLines={singleLineTeamNames ? 1 : undefined}
        />
      ) : null}
    </>
  );

  const heading = hideHeading ? null : (
    <Heading fontSize='lg' flexShrink={0}>
      Results
    </Heading>
  );

  if (!scrollContained) {
    return (
      <Card
        maxW={maxW}
        w='full'
        px={listPadX}
        bg='transparent'
        py={0}
        {...cardProps}
      >
        <VStack spacing={4} align='stretch'>
          {heading}
          {list}
        </VStack>
      </Card>
    );
  }

  return (
    <Card
      maxW={maxW}
      w='full'
      px={listPadX}
      borderRadius={0}
      bg='transparent'
      py={0}
      display='flex'
      flexDirection='column'
      minH={0}
      {...cardProps}
    >
      <Box flex={1} minH={0} display='flex' flexDirection='column' overflow='hidden'>
        <VStack flex={1} minH={0} spacing={4} align='stretch'>
          {heading}
          <Box
            flex={1}
            minH={0}
            overflowY='auto'
            overflowX='hidden'
            sx={{ WebkitOverflowScrolling: 'touch', scrollbarGutter: 'stable' }}
          >
            {list}
          </Box>
        </VStack>
      </Box>
    </Card>
  );
}
